import {css, StyleSheet} from 'aphrodite';
import produce from 'immer';
import React from 'react';

import AddItemButton from 'analysis-tool/data-frame-creation/steps/AddItemButton';
import {DataFrameColumnDef} from 'data-frames/DataFrameTypes';
import FilterRow from 'data-frames/filters/components/FilterRow';
import {
  CONJUNCTION_TO_READABLE,
  DataFrameFilterConjunction,
  DataFrameFilterOperation,
  UserFacingFilter,
  UserFacingFilterOperation,
} from 'data-frames/filters/DataFrameFilterTypes';
import getTestProps from 'test/lib/cypress/getTestProps';
import SelectDropdown from 'ui-library/dropdowns/SelectDropdown';
import Menu from 'ui-library/menus/Menu';
import Option from 'ui-library/menus/Option';

export function getDefaultFilter(column: DataFrameColumnDef): UserFacingFilter {
  return {
    columnSystemName: column.systemName,
    operator: UserFacingFilterOperation.IS_NOT_NULL,
    value: null,
  };
}

export interface InternalDataFrameFilterCreatorProps {
  column: DataFrameColumnDef;
  conjunction: DataFrameFilterConjunction;
  filters: Array<UserFacingFilter>;
  onFiltersChange: (conjunction: DataFrameFilterConjunction, newFilters: Array<UserFacingFilter>) => void;
  disabled?: boolean;
}

/**
 * The main helper component of <DataFrameFilterCreator>, which operates on the user-facing
 * `UserFacingFilterOperation`.
 */
export default function InternalDataFrameFilterCreator({
  column,
  conjunction,
  filters,
  onFiltersChange,
  disabled = false,
}: InternalDataFrameFilterCreatorProps): JSX.Element {
  function handleAddFilter(): void {
    onFiltersChange(conjunction, [...filters, getDefaultFilter(column)]);
  }

  function handleRemoveFilter(index: number): void {
    onFiltersChange(conjunction, [...filters.slice(0, index), ...filters.slice(index + 1)]);
  }

  function handleUpdateFilter(updatedFilter: UserFacingFilter, index: number): void {
    onFiltersChange(
      conjunction,
      produce(filters, (draftFilters) => {
        draftFilters[index] = updatedFilter;
      })
    );
  }

  return (
    <div {...getTestProps({component: 'InternalDataFrameFilterCreator', element: 'root'})}>
      <table>
        <tbody>
          {filters.map((filter, index) => (
            <tr key={index}>
              {filters.length > 1 ? (
                <ConjunctionCell
                  conjunction={conjunction}
                  index={index}
                  onConjunctionChange={(conjunction) => {
                    onFiltersChange(conjunction, filters);
                  }}
                  disabled={disabled}
                />
              ) : null}
              <td className={css(styles.filterRow)}>
                <FilterRow
                  column={column}
                  filter={filter}
                  onDeleteFilter={() => {
                    handleRemoveFilter(index);
                  }}
                  onUpdateFilter={(updatedFilter) => {
                    handleUpdateFilter(updatedFilter, index);
                  }}
                  disabled={disabled}
                />
              </td>
            </tr>
          ))}
        </tbody>
      </table>
      {!disabled && (
        <AddItemButton
          onClick={() => {
            handleAddFilter();
          }}
          label="Add condition"
        />
      )}
    </div>
  );
}

interface ConjunctionCellProps {
  conjunction: DataFrameFilterConjunction;
  index: number;
  onConjunctionChange: (conjunction: DataFrameFilterConjunction) => void;
  disabled?: boolean;
}

function ConjunctionCell({
  conjunction,
  index,
  onConjunctionChange,
  disabled,
}: ConjunctionCellProps): JSX.Element {
  if (index === 1) {
    return (
      <td className={css(styles.filterRow)}>
        <SelectDropdown
          className={css(styles.conjunctionSelector)}
          displayName={CONJUNCTION_TO_READABLE[conjunction]}
          disabled={disabled}
          menu={
            <Menu>
              {[DataFrameFilterOperation.AND, DataFrameFilterOperation.OR].map(
                (conjunction: DataFrameFilterConjunction) => (
                  <Option
                    key={conjunction}
                    value={conjunction}
                    onSelect={() => {
                      onConjunctionChange(conjunction);
                    }}
                  >
                    {CONJUNCTION_TO_READABLE[conjunction]}
                  </Option>
                )
              )}
            </Menu>
          }
        />
      </td>
    );
  }

  return (
    <td className={css(styles.filterRow)}>
      <div className={css(styles.conjunctionLabel)}>
        {index >= 2 ? CONJUNCTION_TO_READABLE[conjunction] : ''}
      </div>
    </td>
  );
}

const styles = StyleSheet.create({
  conjunctionLabel: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'flex-end',
    width: '75px',
    paddingRight: '10px',
  },
  conjunctionSelector: {
    width: '75px',
    paddingRight: '10px',
  },
  filterRow: {
    paddingBottom: '15px',
  },
});
